import React from "react";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import { BsXCircle } from "react-icons/bs";
import GoogleMapReact from "google-map-react";
import moment from "moment";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { Order } from "../../../../models";
import { approveOrder, rejectOrder } from "../../receivedOrdersSlice";
import { updateOrder } from "../../../Orders/ordersSlice";
import { UpdateOrderFormModel } from "../../../../datasource";
import { ConfirmModal } from "../ConfirmModal";
import { triplePSDatasource } from "../../../../datasource";
import { billableExtraDays, extraDayDescription } from "../../../../lib/extraRentalDays";

import "./ReceivedOrderCard.scss";
import { PhoneNumber } from "../../../../components";

// getOrder returns dates as MM-DD-YYYY, but <input type="date"> and the
// updateOrder PUT both expect YYYY-MM-DD.
const toInputDate = (date?: string) =>
  date ? moment(date, "MM-DD-YYYY").format("YYYY-MM-DD") : "";

function OrderMapMarker({ lat, lng }: { lat: number; lng: number }) {
  return (
    <div className="order-map-marker" />
  );
}

export interface AcceptOrderFormModel {
  deliveryFee: number;
  discount: number;
  aditionalItems: AcceptOrderAdditionalItem[];
  // YYYY-MM-DD; persisted via updateOrder (PUT) before approval, since the
  // /approve endpoint does not accept date changes.
  deliveryDate: string;
  pickupDate: string;
}

export interface AcceptOrderAdditionalItem {
  description: string;
  quantity: number;
  basePrice: number;
}

const validationSchema = Yup.object().shape({
  deliveryFee: Yup.number().required("Delivery fee is required"),
  deliveryDate: Yup.string().required("Delivery date is required"),
  pickupDate: Yup.string().required("Pickup date is required"),
  aditionalItems: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required("Description is required"),
      quantity: Yup.number().min(1, "Must be atleast 1"),
      basePrice: Yup.number().positive("Must be positive"),
    })
  ),
});

export interface ReceivedOrderCardProps {
  order: Order;
}

export function ReceivedOrderCard(props: ReceivedOrderCardProps) {
  const dispatch = useAppDispatch();

  const { order } = props;

  const initialDeliveryDate = toInputDate(order.deliveryDate);
  const initialPickupDate = toInputDate(order.pickupDate);

  const [extraDayRate, setExtraDayRate] = React.useState<number>(10);

  React.useEffect(() => {
    triplePSDatasource.getPublicExtraRentalDayPrice()
      .then((rate) => setExtraDayRate(rate))
      .catch(() => { /* leave default 10 */ });
  }, []);

  const initialValues: AcceptOrderFormModel = React.useMemo(() => {
    const seedItems: AcceptOrderAdditionalItem[] = [];
    if (initialDeliveryDate && initialPickupDate) {
      const days = billableExtraDays(initialDeliveryDate, initialPickupDate);
      if (days > 0) {
        seedItems.push({
          description: extraDayDescription(days),
          quantity: days,
          basePrice: extraDayRate,
        });
      }
    }
    return {
      deliveryFee: 0,
      discount: 0,
      aditionalItems: seedItems,
      deliveryDate: initialDeliveryDate,
      pickupDate: initialPickupDate,
    };
  // Re-seed when rate loads (extraDayRate changes from default 10 to actual).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraDayRate]);

  const [showConfirmApproveModal, setShowConfirmApproveModal] = React.useState(false);
  const [showConfirmRejectModal, setShowConfirmRejectModal] = React.useState(false);
  const [pendingForm, setPendingForm] = React.useState<AcceptOrderFormModel | null>(null);

  const approveOrderAPI = useAppSelector(
    (state) => state.receivedOrders.approveOrderAPI
  );
  const rejectOrderAPI = useAppSelector(
    (state) => state.receivedOrders.rejectOrderAPI
  );

  const preventAction = approveOrderAPI.loading || rejectOrderAPI.loading;

  const onRejectPress = () => {
    if (preventAction) return;

    setShowConfirmRejectModal(true);
  };

  const onApprovePress = (form: AcceptOrderFormModel) => {
    if (preventAction) return;

    setPendingForm(form);
    setShowConfirmApproveModal(true);
  };

  const handleConfirmApprove = async () => {
    setShowConfirmApproveModal(false);

    if (!pendingForm) return;

    const form = pendingForm;
    setPendingForm(null);

    // The /approve endpoint can't change dates, so persist any date edits via
    // updateOrder (full-replace PUT) first, then approve. If the date save
    // fails, abort — don't approve with unsaved dates. (The error toast is
    // surfaced by the globally-mounted UpdateOrderModal watching updateOrder.)
    const datesChanged =
      form.deliveryDate !== initialDeliveryDate ||
      form.pickupDate !== initialPickupDate;

    if (datesChanged) {
      const updateForm: UpdateOrderFormModel = {
        id: order.id,
        userId: order.userId,
        name: order.name,
        address: order.address,
        zip: order.zip,
        phoneNumber: order.phoneNumber,
        email: order.email,
        service: order.service,
        dumpsterSize: order.dumpsterSize,
        materialDisposing: order.materialDisposing,
        description: order.description,
        deliveryDate: form.deliveryDate,
        pickupDate: form.pickupDate,
        status: order.status,
        price: order.price,
        deliveryFee: order.deliveryFee,
        po: order.po,
        discount: order.discount,
        specialInstructions: order.specialInstructions,
        customerSpecialInstructions: order.customerSpecialInstructions,
        paymentType: order.paymentType,
        additionalCharges: order.additionalCharges,
        city: order.city,
        latitude: order.latitude,
        longitude: order.longitude,
      };

      try {
        await dispatch(updateOrder(updateForm)).unwrap();
      } catch {
        return;
      }
    }

    dispatch(
      approveOrder({
        id: order.id,
        form,
      })
    );
  };

  const handleCancelApprove = () => {
    setShowConfirmApproveModal(false);
    setPendingForm(null);
  };

  const handleConfirmReject = () => {
    dispatch(
      rejectOrder({
        id: order.id,
      })
    );
    setShowConfirmRejectModal(false);
  };

  const handleCancelReject = () => {
    setShowConfirmRejectModal(false);
  };

  return (
    <div className="received-order-card-component">
      <div className="info-container">
        <div className="info-item">
          <div className="info-label">Name:</div>
          <div className="info-value">{order.name}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Phone Number:</div>
          <div className="info-value">
            <PhoneNumber phoneNumber={order.phoneNumber} />
          </div>
        </div>

        <div className="info-item">
          <div className="info-label">Email:</div>
          <div className="info-value">{order.email}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Address:</div>
          <div className="info-value">{`${order.address}, ${order.zip}`}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Service:</div>
          <div className="info-value">{`${order.service}, ${order.dumpsterSize}`}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Material Disposing:</div>
          <div className="info-value">{order.materialDisposing}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Description:</div>
          <div className="info-value">{order.description}</div>
        </div>

        <div className="info-item">
          <div className="info-label">Instructions:</div>
          <div className="info-value">{order.specialInstructions}</div>
        </div>
      </div>

      <div className="vertical-divider" />

      <Formik
        initialValues={initialValues}
        onSubmit={onApprovePress}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ errors, touched, values }) => (
          <Form className="approve-order-form-component">
            <div className="column">
              <div className="column-title">Dates</div>

              <div className="field-container">
                <div className="label">Delivery date:</div>
                <Field
                  id="deliveryDate"
                  name="deliveryDate"
                  type="date"
                  className="text-input"
                />
                {errors.deliveryDate && touched.deliveryDate && (
                  <div className="form-error">{errors.deliveryDate}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Pickup date:</div>
                <Field
                  id="pickupDate"
                  name="pickupDate"
                  type="date"
                  className="text-input"
                />
                {errors.pickupDate && touched.pickupDate && (
                  <div className="form-error">{errors.pickupDate}</div>
                )}
              </div>

              <div className="horizontal-divider" />

              <div className="column-title">Additional Items</div>

              <div className="row">
                <div className="label width-150">Description:</div>
                <div className="label width-50">Quantity:</div>
                <div className="label width-50">Price:</div>
              </div>

              <FieldArray name="aditionalItems">
                {({ remove, push }) => (
                  <div>
                    {values.aditionalItems.length > 0 &&
                      values.aditionalItems.map((item, index) => (
                        <div className="row" key={index}>
                          <div className="field width-150">
                            <Field
                              className="text-input"
                              name={`aditionalItems.${index}.description`}
                              type="text"
                            />

                            <ErrorMessage
                              name={`aditionalItems.${index}.description`}
                              component="div"
                              className="field-error"
                            />
                          </div>

                          <div className="field width-50">
                            <Field
                              className="text-input"
                              name={`aditionalItems.${index}.quantity`}
                              type="number"
                            />

                            <ErrorMessage
                              name={`aditionalItems.${index}.quantity`}
                              component="div"
                              className="field-error"
                            />
                          </div>

                          <div className="field width-50">
                            <Field
                              className="text-input"
                              name={`aditionalItems.${index}.basePrice`}
                              type="number"
                            />

                            <ErrorMessage
                              name={`aditionalItems.${index}.basePrice`}
                              component="div"
                              className="field-error"
                            />
                          </div>

                          <div
                            className="secondary"
                            onClick={() => remove(index)}
                          >
                            <BsXCircle className="remove-icon" />
                          </div>
                        </div>
                      ))}

                    <div
                      className="add-item-button"
                      onClick={() =>
                        push({ description: "", quantity: 1, basePrice: 0 })
                      }
                    >
                      Add Item
                    </div>
                  </div>
                )}
              </FieldArray>

              <div className="horizontal-divider" />

              <div className="column-title">Additional Charges</div>

              <div className="field-container">
                <div className="label">Delivery fee:</div>
                <Field
                  id="deliveryFee"
                  name="deliveryFee"
                  className="text-input"
                />
                {errors.deliveryFee && touched.deliveryFee && (
                  <div className="form-error">{errors.deliveryFee}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Discount:</div>
                <Field id="discount" name="discount" className="text-input" />
                {errors.discount && touched.discount && (
                  <div className="form-error">{errors.discount}</div>
                )}
              </div>

              <div className="buttons-container">
                <button type="submit" className="button selected">
                  Accept
                </button>
                <div className="button" onClick={onRejectPress}>
                  Reject
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {order.latitude !== 0 && order.longitude !== 0 && (
        <>
          <div className="vertical-divider" />
          <div className="order-map-container">
            <GoogleMapReact
              bootstrapURLKeys={{ key: "AIzaSyCkRYkFYLEOcaEbwmSYJXaQ2jlIttKkl8M" }}
              defaultCenter={{ lat: order.latitude, lng: order.longitude }}
              defaultZoom={12}
            >
              <OrderMapMarker lat={order.latitude} lng={order.longitude} />
            </GoogleMapReact>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={showConfirmApproveModal}
        onConfirm={handleConfirmApprove}
        onCancel={handleCancelApprove}
        customerName={order.name}
        action="approve"
      />

      <ConfirmModal
        isOpen={showConfirmRejectModal}
        onConfirm={handleConfirmReject}
        onCancel={handleCancelReject}
        customerName={order.name}
        action="reject"
      />
    </div>
  );
}
